import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery
} from "@mui/material";
import { styled } from "@mui/system";
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { BarChart2 } from "lucide-react";
import "@fontsource/plus-jakarta-sans";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  transition: "transform 0.3s, box-shadow 0.3s",
  overflow: "hidden",
  height: "100%",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 700,
  marginBottom: "24px",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

// Colors for the charts
const COLORS = {
  BisaDapatBantuan: "#10B981", // Green
  BelumBisaDapatBantuan: "#F43F5E", // Red
  dataTerverifikasi: "#4F46E5" // Blue
};

export const GrafikVerifikasi = () => {
  const [data, setData] = useState([]);
  const [overallData, setOverallData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width:800px)');

  useEffect(() => {
    const fetchVerificationData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_URL}/dataverifikasi`,
          { withCredentials: true }
        );

        const verificationData = response.data.data;
        
        // Filter data by type
        const overallStat = verificationData.find(item => item.statType === "overall");
        const kecamatanStats = verificationData.filter(item => item.statType === "kecamatan");

        setOverallData(overallStat);
        setData(kecamatanStats);
      } catch (error) {
        console.error("Error fetching verification data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVerificationData();
  }, []);

  // Prepare data for bar chart (per kecamatan)
// Prepare data for bar chart (per kecamatan) - MODIFIKASI
const barChartData = {
  labels: data.map(item => item.category),
  datasets: [
    {
      label: 'Data Terverifikasi',
      data: data.map(item => item.dataTerverifikasi === 0 ? null : item.dataTerverifikasi),
      backgroundColor: COLORS.dataTerverifikasi,
      borderColor: COLORS.dataTerverifikasi,
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    },
    {
      label: 'Bisa Dapat Bantuan',
      data: data.map(item => item.BisaDapatBantuan === 0 ? null : item.BisaDapatBantuan),
      backgroundColor: COLORS.BisaDapatBantuan,
      borderColor: COLORS.BisaDapatBantuan,
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    },
    {
      label: 'Belum Bisa Dapat Bantuan',
      data: data.map(item => item.BelumBisaDapatBantuan === 0 ? null : item.BelumBisaDapatBantuan),
      backgroundColor: COLORS.BelumBisaDapatBantuan,
      borderColor: COLORS.BelumBisaDapatBantuan,
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    }
  ]
};

// Bar chart options - MODIFIKASI
const barChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        font: {
          family: "'Plus Jakarta Sans', sans-serif",
          size: isMobile ? 10 : 12
        },
        usePointStyle: true,
      }
    },
    tooltip: {
      backgroundColor: 'white',
      titleColor: '#333',
      bodyColor: '#666',
      borderColor: '#e0e0e0',
      borderWidth: 1,
      cornerRadius: 8,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      font: {
        family: "'Plus Jakarta Sans', sans-serif"
      },
      callbacks: {
        label: function(context) {
          const label = context.dataset.label || '';
          const value = context.parsed.y;
          // Skip tooltip untuk nilai null
          if (value === null || value === undefined) {
            return null;
          }
          return `${label}: ${value}`;
        }
      }
    }
  },
  scales: {
    x: {
      ticks: {
        font: {
          size: isMobile ? 10 : 12,
          family: "'Plus Jakarta Sans', sans-serif"
        },
        maxRotation: isMobile ? 45 : 0,
        minRotation: isMobile ? 45 : 0
      },
      grid: {
        color: '#f0f0f0'
      },
      stacked: false, // Pastikan tidak stacked
    },
    y: {
      beginAtZero: true,
      ticks: {
        font: {
          size: isMobile ? 10 : 12,
          family: "'Plus Jakarta Sans', sans-serif"
        },
        // Untuk memastikan hanya menampilkan angka bulat
        stepSize: 1,
        callback: function(value) {
          if (Number.isInteger(value)) {
            return value;
          }
        }
      },
      grid: {
        color: '#f0f0f0'
      }
    }
  },
  elements: {
    bar: {
      borderRadius: 4,
      borderSkipped: false,
      // INI SOLUSI UTAMA: atur minBarLength ke nilai yang sangat kecil
      minBarLength: 0 // atau 0.1
    }
  },
  // Tambahkan options untuk mengatur interaksi
  interaction: {
    intersect: false,
    mode: 'index',
  }
};

  // Prepare data for doughnut chart (overall)
  const doughnutChartData = overallData ? {
    labels: ['Bisa Dapat Bantuan', 'Belum Bisa Dapat Bantuan'],
    datasets: [
      {
        data: [overallData.BisaDapatBantuan, overallData.BelumBisaDapatBantuan],
        backgroundColor: [COLORS.BisaDapatBantuan, COLORS.BelumBisaDapatBantuan],
        borderColor: [COLORS.BisaDapatBantuan, COLORS.BelumBisaDapatBantuan],
        borderWidth: 2,
        hoverOffset: 4
      }
    ]
  } : { labels: [], datasets: [] };

  // Doughnut chart options
  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            family: "'Plus Jakarta Sans', sans-serif",
            size: isMobile ? 10 : 12
          },
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'white',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        cornerRadius: 8,
        font: {
          family: "'Plus Jakarta Sans', sans-serif"
        },
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    cutout: '40%'
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <SectionTitle sx={{ fontSize: isMobile ? "18px" : "20px" }}>
        <BarChart2 size={isMobile ? 20 : 24} />
        Statistik Verifikasi Data
      </SectionTitle>

      {/* Overall Statistics */}
      <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 3, mb: 4 }}>
        {overallData && (
          <>
            {/* Overall Stats Card */}
            <StyledCard sx={{ flex: 1 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600
                  }}
                >
                  Ringkasan Validasi PB
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: COLORS.dataTerverifikasi, 
                        fontWeight: 700,
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}
                    >
                      {overallData.dataTerverifikasi}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Terverifikasi
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: COLORS.BisaDapatBantuan, 
                        fontWeight: 700,
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}
                    >
                      {overallData.BisaDapatBantuan}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                     Bisa Dapat Bantuan
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: COLORS.BelumBisaDapatBantuan, 
                        fontWeight: 700,
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}
                    >
                      {overallData.BelumBisaDapatBantuan}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                     Belum Bisa Dapat Bantuan
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>

            {/* Doughnut Chart */}
            <StyledCard sx={{ flex: 1 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600
                  }}
                >
                  Persentase Validasi PB
                </Typography>
                <Box sx={{ height: 200, position: 'relative' }}>
                  <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
                </Box>
              </CardContent>
            </StyledCard>
          </>
        )}
      </Box>

      {/* Bar Chart - Verification by Kecamatan */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600
            }}
          >
            Data Validasi PB Per Kecamatan
          </Typography>
          <Box sx={{ height: isMobile ? 300 : 400, position: 'relative' }}>
            <Bar data={barChartData} options={barChartOptions} />
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default GrafikVerifikasi;